import React, { useEffect, useState } from 'react'
import { Box, Card } from '@mui/material'
import {
  Typography,
  Button,
  Grid,
  CircularProgress,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  MenuItem,
  FormHelperText,
  Checkbox,
  Divider
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useConfirm } from 'material-ui-confirm'
import {
  addAnnouncement,
  fetchAnnouncementById,
  resetAnnouncement,
  sendAnnouncement,
  updateAnnouncementById
} from 'store/announcements'
import { Form, Formik } from 'formik'
import { announcementFormSchema } from 'schemas/announcement'
import { isEmpty } from 'lodash'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import ProgressButton from 'components/ProgressButton'
import CustomBox from 'components/Form/CustomBox'
import DateTimeInput from 'components/DateTimeInput'
import { AnnouncementStatus, AttendeeRoles } from 'constants/index'
import { getDefaultTimezone } from 'utils/times'
import { colors } from 'utils/colors'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

const recipients = Object.values(AttendeeRoles).map((value) => value)

const AnnouncementForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const confirm = useConfirm()
  const history = useHistory()

  const { announcementId } = useParams()
  const [scheduledProgress, setScheduledProgress] = useState(false)
  const { conference } = useSelector((state) => state.conference)
  const { announcement, inProgress } = useSelector((state) => ({
    inProgress: state.announcements.inProgress,
    announcement: state.announcements.announcement
  }))

  const initialValues = {
    announcementText: '',
    isScheduled: false,
    scheduledOn: '',
    status: AnnouncementStatus.SCHEDULED,
    recipients: []
  }

  if (announcement && announcement.id === announcementId) {
    initialValues.announcementText = announcement.announcementText
    initialValues.scheduledOn = moment(announcement.scheduledOn).tz(conference?.timezone)
    initialValues.status = announcement.status
    initialValues.recipients = announcement?.recipients ?? []
  }

  const goBack = () => {
    dispatch(resetAnnouncement())
    history.goBack()
  }

  useEffect(() => {
    if (announcementId) {
      dispatch(fetchAnnouncementById(announcementId))
    }
  }, [announcementId])

  const getMinimumDate = (providedDate, timezone) => {
    const currentDate = moment().tz(timezone)
    if (currentDate.isAfter(moment(providedDate).tz(timezone))) {
      return currentDate
    } else {
      return moment(providedDate).tz(timezone)
    }
  }
  const getMaxDate = (endDate, timezone) => {
    const utcTime = moment.utc(endDate)
    const time = utcTime.tz(timezone).format('YYYY-MM-DD')
    return moment.tz(time, timezone)
  }

  const handleChange = (event, setFieldValue) => {
    const value = event.target.value
    if (value.length > 0 && !value.includes('')) {
      setFieldValue('recipients', value)
    } else if (value.length === 0) {
      setFieldValue('recipients', [])
    }
  }

  const handleSelectAll = (selectedRecipients, setFieldValue) => {
    if (selectedRecipients.length > 0 && selectedRecipients.length === recipients.length) {
      setFieldValue('recipients', [])
    } else {
      setFieldValue('recipients', recipients)
    }
  }

  const onSubmit = async ({ announcementText, isScheduled, scheduledOn, recipients }) => {
    const nowDateTime = moment.utc()
    nowDateTime.seconds(0)
    nowDateTime.milliseconds(0)

    const scheduledTimeInUTC = moment(scheduledOn).utc()
    scheduledTimeInUTC.seconds(0)
    scheduledTimeInUTC.milliseconds(0)

    const payload = {
      status: isScheduled ? AnnouncementStatus.SCHEDULED : AnnouncementStatus.SENT,
      announcementText: announcementText.trim(),
      publishedOn: nowDateTime.valueOf(),
      scheduledOn: isScheduled ? scheduledTimeInUTC.valueOf() : '',
      recipients
    }
    if (
      announcementId &&
      announcement?.scheduledOn &&
      announcement?.status === AnnouncementStatus.SENT
    ) {
      payload.status = AnnouncementStatus.SENT
    }

    if (announcementId) {
      payload.publishedOn = announcement?.publishedOn
      await dispatch(updateAnnouncementById(announcementId, payload))
      toast(`Announcement updated successfully`, {
        type: 'success'
      })
    } else {
      if (isScheduled) {
        setScheduledProgress(true)
        await dispatch(addAnnouncement(payload))
        setScheduledProgress(false)
      } else {
        await confirm({
          title: 'Create Announcement',
          description: `Are you sure you want to Create this Announcement?`
        })
        const announcementId = await dispatch(addAnnouncement(payload))
        if (announcementId) {
          await dispatch(sendAnnouncement(announcementId))
        }
      }
      toast(`${isScheduled ? 'Scheduled' : ''} Announcement created successfully`, {
        type: 'success',
        autoClose: 3000
      })
    }

    goBack()
  }

  return (
    <CustomBox>
      <ViewHeader title={announcementId ? 'Edit Announcement' : 'Create Announcement'}>
        <div style={{ marginRight: 10 }}>
          <ActionBar actionText="Back" onAction={() => goBack()} />
        </div>
      </ViewHeader>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={announcementFormSchema}
        onSubmit={onSubmit}>
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.label}>Announcement</label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.announcementText}
                    onChange={(value) => setFieldValue('announcementText', value)}
                    placeholder="Write announcement here..."
                  />
                  <ErrorMessage message={touched?.announcementText && errors?.announcementText} />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.recipients && touched.recipients}>
                  <InputLabel>Recipients</InputLabel>
                  <Select
                    multiple
                    name="recipients"
                    value={values.recipients}
                    onChange={(event) => handleChange(event, setFieldValue)}
                    input={<OutlinedInput id="select-multiple-chip" label="Recipients" />}
                    renderValue={(selected) => (
                      <Box className={classes.flexBox}>
                        {selected.map((value) => (
                          <Chip
                            className={classes.chip}
                            key={value}
                            label={recipients?.find((recipient) => recipient === value)}
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}>
                    <MenuItem
                      value=""
                      className={classes.menu}
                      onClick={() => handleSelectAll(values.recipients, setFieldValue)}>
                      <Typography variant="body2">
                        {values.recipients.length > 0 &&
                        values.recipients.length === recipients.length
                          ? 'Unselect All'
                          : 'Select All'}
                      </Typography>
                    </MenuItem>
                    <Divider />
                    {Object.entries(AttendeeRoles).map(([key, value]) => (
                      <MenuItem value={value} key={key} className={classes.menu}>
                        <Checkbox
                          checked={values.recipients?.indexOf(value ?? '') > -1}
                          size="small"
                          className={classes.checkbox}
                        />
                        <Typography variant="body2">{value}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.recipients && <FormHelperText>{errors.recipients}</FormHelperText>}
                </FormControl>
              </Grid>

              {(announcementId && announcement?.scheduledOn) || !announcementId ? (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: '1rem', borderRadius: '8px' }}>
                    <Typography sx={{ fontWeight: '500' }} variant="h6">
                      Schedule Announcement
                    </Typography>
                    <Box sx={{ mt: '0.75rem' }}>
                      {(announcementId &&
                        announcement?.scheduledOn &&
                        announcement?.status !== AnnouncementStatus.SENT) ||
                      !announcementId ? (
                        <Box>
                          <DateTimeInput
                            value={values.scheduledOn}
                            dateLabel="Schedule Date Time"
                            timeLabel="Schedule Date Time"
                            name="scheduledOn"
                            is24hrFormat={conference?.is24hrFormat}
                            minDate={
                              !isEmpty(conference) &&
                              getMinimumDate(conference?.startDate, conference?.timezone)
                            }
                            maxDate={
                              !isEmpty(conference) &&
                              getMaxDate(conference?.endDate, conference?.timezone)
                            }
                            onChange={(value) => setFieldValue('scheduledOn', value)}
                          />
                          <Typography className={classes.timezoneText}>
                            Conference Timezone is in {conference?.timezone}.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ p: '1rem', backgroundColor: '#c8e6c9', borderRadius: '4px' }}>
                          <Typography variant="body1" className={classes.boldFont}>
                            This Announcement was sent at{' '}
                            {announcement?.scheduledOn &&
                              announcement?.status === AnnouncementStatus.SENT &&
                              moment(announcement?.scheduledOn)
                                .tz(getDefaultTimezone(conference?.timezone))
                                .format('MMM DD, YYYY hh:mm A')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ) : null}
            </Grid>
            <Grid container justifyContent="flex-end" spacing={3}>
              <Grid item>
                <Button variant="contained" size="large" onClick={() => goBack()}>
                  Cancel
                </Button>
              </Grid>
              {(announcementId && announcement?.scheduledOn) || !announcementId ? (
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {scheduledProgress && (
                      <CircularProgress
                        color="secondary"
                        style={{ marginRight: 15 }}
                        variant="indeterminate"
                        size={24}
                      />
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      onClick={() => {
                        setFieldValue('isScheduled', true)
                        if (
                          announcementId &&
                          announcement?.scheduledOn &&
                          announcement?.status === AnnouncementStatus.SENT
                        ) {
                          setFieldValue('status', AnnouncementStatus.SENT)
                        } else {
                          setFieldValue('status', AnnouncementStatus.SCHEDULED)
                        }
                      }}
                      disabled={inProgress || scheduledProgress}
                      className={classes.customButton}>
                      save scheduled announcement
                    </Button>
                  </Box>
                </Grid>
              ) : null}
              {(announcementId && !announcement?.scheduledOn) || !announcementId ? (
                <Grid item>
                  <ProgressButton
                    type="submit"
                    label={announcementId ? 'Save' : 'Save and Send Now'}
                    inProgress={inProgress && !scheduledProgress}
                    disabled={inProgress}
                    onClick={() => {
                      setFieldValue('isScheduled', false)
                      setFieldValue('status', AnnouncementStatus.SENT)
                    }}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Form>
        )}
      </Formik>
    </CustomBox>
  )
}
export default AnnouncementForm

const useStyles = makeStyles(() => ({
  customButton: {
    backgroundColor: '#ffad17',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#da9210'
    }
  },
  boldFont: {
    fontWeight: 'bold'
  },
  timezoneText: {
    marginTop: '0.3rem',
    fontSize: '12px',
    display: 'inline-flex',
    justifyContent: 'center',
    backgroundColor: '#FEE01C',
    color: '#000000',
    fontWeight: 'bold',
    padding: '0.25rem 3rem'
  },
  menu: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  checkbox: {
    padding: '2px'
  },
  chip: {
    background: colors.indigo,
    color: 'white'
  },
  flexBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060'
  }
}))
